import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, User, Calendar, Users, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminAccountApprovals = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { toast } = useToast();

  useEffect(() => {
    fetchPendingAccounts();
  }, [currentPage]);

  const fetchPendingAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/accounts/pending?page=${currentPage}&limit=20`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setAccounts(response.data.accounts || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch pending accounts:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load pending accounts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (account) => {
    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/accounts/${account._id}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Success!",
        description: `Account approved for ${account.firstName} ${account.lastName}`,
      });

      // Remove from list
      setAccounts(prev => prev.filter(a => a._id !== account._id));
    } catch (error) {
      console.error('Failed to approve account:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to approve account",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAccount) return;

    try {
      setProcessing(true);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/admin/accounts/${selectedAccount._id}/reject?reason=${encodeURIComponent(rejectionReason)}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast({
        title: "Account Rejected",
        description: `Account rejected for ${selectedAccount.firstName} ${selectedAccount.lastName}`,
      });

      // Remove from list
      setAccounts(prev => prev.filter(a => a._id !== selectedAccount._id));
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedAccount(null);
    } catch (error) {
      console.error('Failed to reject account:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reject account",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-7 h-7 text-mint-500" />
          Pending Account Approvals
        </h1>
        <p className="text-gray-600 mt-1">Review and approve new user registrations</p>
      </div>

      {/* Stats Card */}
      <div className="mb-6 bg-gradient-to-r from-mint-50 to-teal-50 border border-mint-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Clock className="w-6 h-6 text-mint-600" />
            <div>
              <p className="text-sm text-gray-600">Pending Approvals</p>
              <p className="text-2xl font-bold text-gray-900">{accounts.length}</p>
            </div>
          </div>
          <Button onClick={fetchPendingAccounts} variant="outline" size="sm">
            Refresh
          </Button>
        </div>
      </div>

      {/* Accounts List */}
      {accounts.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900">All caught up!</h3>
            <p className="text-gray-600 mt-2">No pending account approvals at this time.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => (
            <Card key={account._id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* User Info */}
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-mint-500 to-teal-500 flex items-center justify-center text-white text-xl font-bold">
                      {account.firstName?.[0]}{account.lastName?.[0]}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {account.firstName} {account.lastName}
                      </h3>
                      <p className="text-gray-600 text-sm">{account.email}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {account.gender === 'male' ? 'Male' : 'Female'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {calculateAge(account.dob)} years old
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Submitted: {formatDate(account.basicInfoSubmittedAt || account.createdAt)}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(account)}
                      disabled={processing}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedAccount(account);
                        setShowRejectModal(true);
                      }}
                      disabled={processing}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="py-2 px-4 text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold">Reject Account</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to reject the account for{' '}
              <strong>{selectedAccount?.firstName} {selectedAccount?.lastName}</strong>?
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason (optional)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={3}
                placeholder="Enter reason for rejection..."
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedAccount(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={processing}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {processing ? 'Rejecting...' : 'Reject Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccountApprovals;
